import { useEffect, useRef, useState } from "react";
import type { StorageOrder } from "../api";
import { Logo } from "../components/Logo";
import { formatBytes, formatDays } from "../format";
import { useConfirmPayment } from "../queries";
import { Link, useLocation } from "../router";

/**
 * Back from wanqu.me: app.ccnote.me/billing/return?wanqu_payment=…&wanqu_sig=… (or ?cancelled=1).
 * The API checks the redirect and asks wanqu.me for the payment's real state; if it's still
 * being confirmed, we ask again for a little while (the webhook will finish it either way).
 */
export function BillingReturn() {
  const { params } = useLocation();
  const payment = params.get("wanqu_payment") ?? "";
  const sig = params.get("wanqu_sig") ?? "";
  const confirm = useConfirmPayment();
  const [order, setOrder] = useState<StorageOrder | null>(null);
  const [cancelled, setCancelled] = useState(params.get("cancelled") === "1" && !payment);
  const tries = useRef(0);

  useEffect(() => {
    if (!payment || !sig) return;
    const check = () =>
      confirm.mutate(
        { payment, sig },
        {
          onSuccess: (r) => {
            setOrder(r.order);
            if (r.redirectStatus === "cancelled") setCancelled(true);
            else if (r.order.status === "pending" && tries.current++ < 10) setTimeout(check, 3000);
          },
        },
      );
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment, sig]);

  return (
    <main className="settings billing-return">
      <header className="settings-header">
        <Link to="/" className="btn btn-small">
          ← Notes
        </Link>
        <Logo />
      </header>
      <section className="panel">
        {cancelled ? (
          <>
            <h1>Payment cancelled</h1>
            <p className="muted">Nothing was charged. You can buy storage any time from Settings.</p>
          </>
        ) : confirm.isError ? (
          <>
            <h1>We couldn't confirm this payment here</h1>
            <p className="error">{(confirm.error as Error).message}</p>
            <p className="muted">If you paid, your storage is added as soon as the payment service tells us; check Settings → Storage in a minute.</p>
          </>
        ) : order?.status === "paid" ? (
          <>
            <h1>✓ {order.renewsOrderId ? "Storage pack renewed" : "Storage added"}</h1>
            <p>
              <strong>+{formatBytes(order.bytes)}</strong> {order.renewsOrderId ? "renewed" : "added to your account"} for {formatDays(order.days)}. See the end date
              in Settings → Storage.
            </p>
          </>
        ) : order && order.status !== "pending" ? (
          <>
            <h1>Payment {order.status}</h1>
            <p className="muted">No storage was added. You can try again from Settings.</p>
          </>
        ) : (
          <>
            <h1>Confirming your payment…</h1>
            <p className="muted">This usually takes a few seconds.</p>
          </>
        )}
        <Link to="/settings#storage" className="btn">
          Go to Settings → Storage
        </Link>
      </section>
    </main>
  );
}
